import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  cta: {
    text: string;
    link: string;
  };
}

const slides: HeroSlide[] = [
  {
    id: '1',
    title: 'PadBox GS',
    subtitle: 'One Controller for Every Game',
    description: 'Premium gaming controller with GP2040-CE firmware and Hall Effect sticks',
    image: '/images/products/padbox-gs/hero-purple.jpg',
    cta: {
      text: 'Shop Now',
      link: '/product/padbox-gs-essential',
    },
  },
  {
    id: '2',
    title: 'Hing3DBox Leverless',
    subtitle: 'Precision Fighting Performance',
    description: 'Professional-grade leverless controller for competitive gaming',
    image: '/images/products/hing3dbox/hero-leverless.jpg',
    cta: {
      text: 'Explore',
      link: '/product/hing3dbox-leverless',
    },
  },
  {
    id: '3',
    title: 'Custom Builds',
    subtitle: 'Build Your Perfect Controller',
    description: 'DIY kits and components for ultimate customization',
    image: '/images/products/diy/hero-components.jpg',
    cta: {
      text: 'Learn More',
      link: '/shop',
    },
  },
];

export const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      {/* Subtle pixelated grid pattern */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}
      />

      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/5" />

      <div className="container mx-auto px-4 md:px-8 py-12 md:py-20 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 lg:pr-8"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div>
                  <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4">
                    {slides[currentSlide].title}
                  </h1>
                  <p className="text-2xl md:text-3xl lg:text-4xl text-brand-accent font-semibold">
                    {slides[currentSlide].subtitle}
                  </p>
                </div>

                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                  {slides[currentSlide].description}
                </p>

                <div className="flex gap-4 pt-4">
                  <Link to={slides[currentSlide].cta.link}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        size="lg"
                        className="bg-brand-accent hover:bg-brand-accent/90 text-white text-lg px-8 py-6 shadow-lg shadow-brand-accent/30 font-bold"
                      >
                        {slides[currentSlide].cta.text}
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/shop">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-2 border-white/20 bg-white/5 hover:bg-white/10 text-white text-lg px-8 py-6 font-bold backdrop-blur-sm"
                      >
                        View All Products
                      </Button>
                    </motion.div>
                  </Link>
                </div>

                {/* Dot indicators */}
                <div className="flex gap-3 pt-4">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-brand-accent w-12 shadow-md shadow-brand-accent/50'
                          : 'bg-white/30 w-2 hover:bg-white/50'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Right Column - Image Carousel */}
          <div className="relative h-[400px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden group">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className="w-full h-full object-cover"
                />
                {/* Subtle gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </motion.div>
            </AnimatePresence>

            {/* Navigation arrows - only visible on hover */}
            <Button
              variant="ghost"
              size="icon"
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm border border-white/20 w-12 h-12 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm border border-white/20 w-12 h-12 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
